import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { galleryImages, instagramHandle } from '../data/mock';
import { ExternalLink, Instagram } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';

const Gallery = () => {
  const { t, language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState(language === 'fr' ? 'Tous' : 'All');

  const categories = t.gallery.categories;

  const filteredImages = activeCategory === 'Tous' || activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter(img => {
        const categoryMap = {
          'Gel': 'Gel',
          'Nail Art': 'Nail Art',
          'Classique': 'Classique',
          'Classic': 'Classique',
          'Mariée': 'Mariée',
          'Bridal': 'Mariée'
        };
        return img.category === categoryMap[activeCategory];
      });

  return (
    <section id="gallery" className="py-24 bg-white relative">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-rose-400 text-sm tracking-widest uppercase font-medium">
            Portfolio
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 mt-3 mb-4">
            {t.gallery.title}
          </h2>
          <div className="w-16 h-1 bg-rose-300 mx-auto mb-6" />
          <p className="text-stone-600 text-lg">{t.gallery.subtitle}</p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-5 py-2 rounded-full text-sm transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-stone-800 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-rose-100 hover:text-rose-600'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {filteredImages.map((image, index) => (
            <Dialog key={image.id}>
              <DialogTrigger asChild>
                <div
                  className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                    <span className="text-white text-sm font-light tracking-wide">
                      {image.category}
                    </span>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-3xl p-0 bg-transparent border-0">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto rounded-xl"
                />
              </DialogContent>
            </Dialog>
          ))}
        </div>

        {/* Instagram Link */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            className="border-rose-300 text-rose-500 hover:bg-rose-50 hover:text-rose-600 rounded-full px-8 group"
            onClick={() => window.open(`https://www.instagram.com/${instagramHandle}/`, '_blank')}
          >
            <Instagram className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
            {t.gallery.viewMore}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
